import axios from "axios";

export function clearClientSession(redirectTo = "/login") {
  try {
    const theme = window.localStorage.getItem("theme");
    window.localStorage.clear();
    if (theme) window.localStorage.setItem("theme", theme);
  } catch (error) {
    console.warn("Failed to clear localStorage during forced logout:", error);
  }

  try {
    window.sessionStorage.clear();
  } catch (error) {
    console.warn("Failed to clear sessionStorage during forced logout:", error);
  }

  if (window.location.pathname !== redirectTo) {
    window.location.replace(redirectTo);
  }
}

function getBackendURL() {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  const frontendPort = process.env.REACT_APP_FRONTEND_PORT || "3000";
  const backendPort = process.env.REACT_APP_BACKEND_PORT || "5000";

  if (window.location.hostname.includes("devtunnels.ms")) {
    const backendHost = window.location.hostname.replace(`-${frontendPort}`, `-${backendPort}`);
    return `${window.location.protocol}//${backendHost}/api`;
  }

  if (window.location.hostname.includes("loca.lt")) {
    const savedBackend = localStorage.getItem("backend_tunnel_url");
    if (savedBackend) {
      return savedBackend;
    }

    const backendUrl = prompt(
      "Enter your backend tunnel URL (e.g., https://abc123.loca.lt/api):\n" +
        "You can find this in your backend tunnel window.",
    );

    if (backendUrl) {
      localStorage.setItem("backend_tunnel_url", backendUrl);
      return backendUrl;
    }

    return `http://localhost:${backendPort}/api`;
  }

  return `http://localhost:${backendPort}/api`;
}

function validateBackendURL(url) {
  try {
    new URL(url);
    return url;
  } catch (error) {
    console.warn("⚠️ Invalid backend URL detected:", url);
    const backendPort = process.env.REACT_APP_BACKEND_PORT || "5000";
    return `http://localhost:${backendPort}/api`;
  }
}

const API = axios.create({
  baseURL: validateBackendURL(getBackendURL()),
  headers: { "Content-Type": "application/json" },
});

API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers["Cache-Control"] = "no-cache";
    config.headers.Pragma = "no-cache";
    config.timeout = config.timeout || 30000;
    return config;
  },
  (error) => Promise.reject(error),
);

API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const responseStatus = error.response?.status;
    const responseMessage = String(
      error.response?.data?.message || error.response?.data?.msg || error.message || "",
    );
    const hasActiveSession = Boolean(localStorage.getItem("token"));
    const isAuthRoute = originalRequest?.url?.includes("/auth/");
    const shouldForceLogout =
      hasActiveSession &&
      !isAuthRoute &&
      (/user no longer exists/i.test(responseMessage) ||
        (responseStatus === 401 && originalRequest?.url?.includes("/users/me")));

    if (shouldForceLogout) {
      console.warn("Forcing logout due to invalid user session:", {
        url: originalRequest?.url,
        status: responseStatus,
        message: responseMessage,
      });
      clearClientSession();
      return Promise.reject(error);
    }

    if (error.code === "ECONNABORTED" || error.message.includes("timeout")) {
      return Promise.reject(error);
    }

    if (
      error.code === "ECONNREFUSED" ||
      error.code === "ERR_NETWORK" ||
      (error.response && error.response.status === 0)
    ) {
      if (!originalRequest._retryCount) {
        originalRequest._retryCount = 0;
      }

      if (originalRequest._retryCount < 3) {
        originalRequest._retryCount++;
        await new Promise((resolve) => setTimeout(resolve, 1000));
        return API(originalRequest);
      } else {
        console.error("❌ API connection failed after 3 retries:", error.message);
        if (error.config.url.includes("/auth/login")) {
          throw new Error(
            "Unable to connect to server. Please check if the backend is running and try again.",
          );
        }
      }
    }

    return Promise.reject(error);
  },
);

export default API;
export function fetchSymbolsOnboarding(payload) {
  return API.post("/trading/fetch-symbols", payload);
}

export function fetchCurrentUser() {
  return API.get("/users/me");
}

export function fetchNetwork() {
  return API.get("/users/network");
}

export function fetchRecentSessions() {
  return API.get("/users/sessions");
}

export function saveNetworkShares(level1, level2) {
  return API.post("/users/network/shares", { level1, level2 });
}

export function fetchPositions(terminal, brokerId) {
  return API.get("/trading/positions", {
    params: { terminal, id: brokerId },
  });
}

export function fetchBalance(terminal, brokerId) {
  return API.get("/trading/balance", {
    params: { terminal, id: brokerId },
  });
}

export function fetchSymbols(terminal, brokerId) {
  return API.get("/trading/symbols", {
    params: { terminal, id: brokerId },
  });
}

export function fetchPremiumData(companyName, accountSetId, limit = 100) {
  return API.get(`/premium-data/${companyName}/${accountSetId}`, {
    params: { limit },
  });
}

export function fetchMultipleSymbols(requests) {
  return API.post("/trading/symbols/batch", { requests });
}

export function fetchMt5Symbols(id) {
  return fetchSymbols("MT5", id);
}

export function fetchMt4Symbols(id) {
  return fetchSymbols("MT4", id);
}

export function saveTradingAccount(terminal, token) {
  return API.post("/users/account", { terminal, token });
}

export async function fetchSymbolsCache({ broker1, broker2, terminal }) {
  const params = new URLSearchParams();
  if (broker1) params.set("broker1", broker1);
  if (broker2) params.set("broker2", broker2);
  if (terminal) params.set("terminal", terminal);

  const res = await fetch(`/api/symbols/cache?${params.toString()}`, {
    credentials: "include",
  });
  if (!res.ok) throw new Error("symbols_fetch_failed");
  return res.json();
}
